import type { LucideIcon } from 'lucide-react';
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Music2,
  Pin,
  Rss,
  Twitter,
  Youtube,
} from 'lucide-react';
import type {
  SocialAdPlacement,
  SocialPlatform,
  SocialPostStatus,
  SocialPostType,
} from '@/types/database';

/**
 * Display metadata for the Social Content Manager (SOCIAL.md). Literal
 * Tailwind class maps so the JIT picks the classes up at build time —
 * same pattern as src/features/calendar/colors.ts.
 */

// Launch order per SOCIAL.md §9.2: IG / FB / TikTok / YouTube first,
// X / LinkedIn / Pinterest after. `blog` and `newsletter` (migration 84)
// are owned channels: planned on the same calendar, published manually
// (Shopify blog / email tooling) — the Phase 2 scheduler skips them.
export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'x',
  'linkedin',
  'pinterest',
  'blog',
  'newsletter',
];

export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  blog: 'Blog',
  newsletter: 'E-newsletter',
};

// lucide has no TikTok / X / Pinterest brand glyphs — Music2, Twitter and
// Pin are the closest stand-ins until we drop in brand SVGs.
export const PLATFORM_ICONS: Record<SocialPlatform, LucideIcon> = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: Music2,
  youtube: Youtube,
  x: Twitter,
  linkedin: Linkedin,
  pinterest: Pin,
  blog: Rss,
  newsletter: Mail,
};

export const SOCIAL_STATUSES: SocialPostStatus[] = [
  'draft',
  'in_review',
  'approved',
  'scheduled',
  'publishing',
  'published',
  'failed',
];

export const STATUS_LABELS: Record<SocialPostStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
};

/** Status pill (badge) classes. */
export const STATUS_PILL_CLASS: Record<SocialPostStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  in_review: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-teal-100 text-teal-800 border-teal-300',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
  publishing: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  published: 'bg-green-100 text-green-800 border-green-300',
  failed: 'bg-red-100 text-red-800 border-red-300',
};

/** Small dot next to the status label on calendar cards. */
export const STATUS_DOT_CLASS: Record<SocialPostStatus, string> = {
  draft: 'bg-slate-400',
  in_review: 'bg-amber-500',
  approved: 'bg-teal-500',
  scheduled: 'bg-blue-500',
  publishing: 'bg-indigo-500',
  published: 'bg-green-600',
  failed: 'bg-red-500',
};

/**
 * Calendar card chip per post type — ads get the distinct violet accent
 * (SOCIAL.md §5) so paid vs. organic cadence reads at a glance.
 */
export const TYPE_CHIP_CLASS: Record<SocialPostType, string> = {
  organic: 'bg-sky-50 text-sky-950 border-sky-400 hover:bg-sky-100',
  ad: 'bg-violet-100 text-violet-950 border-violet-500 hover:bg-violet-200',
};

export const TYPE_LABELS: Record<SocialPostType, string> = {
  organic: 'Organic',
  ad: 'Ad',
};

export const AD_PLACEMENTS: SocialAdPlacement[] = ['feed', 'story', 'reel', 'other'];

export const AD_PLACEMENT_LABELS: Record<SocialAdPlacement, string> = {
  feed: 'Feed',
  story: 'Story',
  reel: 'Reel',
  other: 'Other',
};

/**
 * Drag-to-reschedule is allowed only in these states (SOCIAL.md §2.1) —
 * blocked once a post is publishing / published / failed.
 */
export const RESCHEDULABLE_STATUSES: ReadonlySet<SocialPostStatus> = new Set([
  'draft',
  'in_review',
  'approved',
  'scheduled',
]);
